/**
 * The auction interface, generated from `actions.legalCalls` and nothing else.
 *
 * The server sends a finite list of what this viewer may call right now, each
 * with a label, a plain-English explanation and a value. That list *is* the
 * panel: there is no per-game validation here, no ladder of rules, and a call
 * that is not in the list simply has no control. A game may supply a layout
 * (500's printed bid table) to place those same calls; without one they render
 * as a list.
 *
 * ## The auction happens at the table, not in a strip above it
 *
 * It used to be four columns in reading order with a rail under them. That is
 * a correct record of the auction and a poor picture of it: a bid arriving is
 * a *player* speaking, and the strip gave every player the same anonymous
 * column. The seats are already laid out — the same four positions the trick
 * is played into, off the same `.seat-n/w/e/s` geometry — so the calls go
 * there instead, and the turn is watched moving round the table rather than
 * along a line. Trickster does the same thing for the same reason: its bids
 * are chips pinned to each player, not a list.
 *
 * The ladder is there for the whole auction, live only on your call, so the
 * screen keeps one shape as the turn goes round.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { Seat } from '@cards/shared'
import { BotIcon, SuitText } from './atoms'
import { SeatFan } from './Card'
import { cardSrc } from '../cards/assets'
import { prefersReducedMotion, useSettings } from '../settings/settings'
import type { AuctionClose } from './TableScreen'
import type { AuctionSeatCall, LegalCall } from '../games/types'
import type { TableView } from './view'
import { coachCall, useCoach } from '../coach/useCoach'

export function BidPanel({
  view,
  onCall,
  locked,
  closing = null,
  redealing = false,
}: {
  view: TableView
  onCall: (call: unknown) => void
  locked: boolean
  /** The auction has closed and the table is showing who won it (TableScreen). */
  closing?: AuctionClose | null
  /** Everyone passed: the hand is being thrown in and dealt again. */
  redealing?: boolean
}): JSX.Element {
  const calls = (view.actions?.legalCalls ?? []) as readonly LegalCall[]
  const gameUi = view.gameUi
  const bids = calls.filter((c) => !gameUi.isPass(c))
  const pass = calls.find((c) => gameUi.isPass(c)) ?? null
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  // The lowest live rung is preselected so the explain bar always has something
  // to explain — the design's bidding screen is never blank.
  useEffect(() => {
    setSelectedKey((prev) => {
      if (prev && calls.some((c) => c.key === prev)) return prev
      return bids[0]?.key ?? null
    })
  }, [calls, bids])

  // Coach mode picks the rung for you — once per position, so a player who
  // moves off it is not dragged back.
  const coachPick = coachCall(view, useCoach(view))
  const coachApplied = useRef<string | null>(null)
  useEffect(() => {
    if (!coachPick || gameUi.isPass(coachPick)) return
    const at = `${view.snapshot.seq}:${coachPick.key}`
    if (coachApplied.current === at) return
    coachApplied.current = at
    setSelectedKey(coachPick.key)
  }, [coachPick?.key, view.snapshot.seq])
  const coachPass = coachPick !== null && gameUi.isPass(coachPick)

  const mine = view.isMyTurn && !closing && !redealing
  const selected = mine ? (bids.find((c) => c.key === selectedKey) ?? null) : null
  const Layout = gameUi.BidLayout

  // The ladder is on screen for the whole auction, and so are the buttons.
  // It used to appear only on your own call, which gave the table all the
  // height the ladder left it on everyone else's — so every seat jumped up
  // the screen when your turn came and back down when it went. Now the table
  // is one size throughout: on somebody else's call the ladder is the same
  // ladder, every rung dead but still pencilled with who bid what, and the
  // line above it says who we are waiting on.
  return (
    <section className="bidding">
      <AuctionTable view={view} closing={closing} redealing={redealing} />

      {closing ? (
        <AuctionWon view={view} closing={closing} />
      ) : redealing ? (
        <Redeal view={view} />
      ) : mine ? (
        <Explain
          view={view}
          selected={selected}
          passOnly={bids.length === 0 && pass !== null}
          coach={coachPick === null ? null : coachPass ? 'pass' : selected?.key === coachPick.key ? 'this' : 'other'}
        />
      ) : (
        <Waiting view={view} />
      )}

      <div className={mine ? 'bid-board' : 'bid-board idle'} aria-disabled={!mine}>
        {Layout ? (
          <Layout
            view={view}
            calls={mine ? bids : []}
            selected={selected}
            onSelect={(call) => mine && setSelectedKey(call?.key ?? null)}
          />
        ) : (
          <div className="call-list">
            {bids.map((call) => (
              <button
                key={call.key}
                className={`cell${call.key === selectedKey ? ' sel' : ''}`}
                disabled={!mine}
                onClick={() => setSelectedKey(call.key === selectedKey ? null : call.key)}
              >
                {call.label}
                {call.value === undefined ? null : <small>{call.value}</small>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pass is the lesser half of the row and Bid takes the width, so the
          two are not a choice between equals. Both are there on every call,
          live only on yours. */}
      <div className="actions">
        <button
          className={mine && coachPass ? 'btn btn-secondary coach-pick' : 'btn btn-secondary'}
          disabled={!mine || !pass || locked}
          onClick={() => pass && onCall(pass.call)}
        >
          Pass
        </button>
        <button
          className="btn btn-primary"
          disabled={!mine || !selected || locked}
          onClick={() => selected && onCall(selected.call)}
        >
          {selected ? gameUi.bidButtonLabel(selected) : 'Bid'}
        </button>
      </div>
    </section>
  )
}

/** The line above the ladder while somebody else is calling: Trickster's
 *  "Waiting for *name* to bid", where the explanation of your own call goes. */
function Waiting({ view }: { view: TableView }): JSX.Element {
  const who = view.toAct === null ? null : view.name(view.toAct)
  return (
    <div className="explain" role="status">
      <h3 className="waiting-head">
        {who ? (
          <>
            Waiting for <b>{who}</b> to bid
          </>
        ) : (
          'Bidding'
        )}
      </h3>
      <p>{view.gameUi.auctionRule}</p>
    </div>
  )
}

/**
 * Everyone passed: said where the table says who it is waiting on, while the
 * server holds the dead hand for a moment before dealing again. The passes
 * stay on every seat, so the table shows why as well as saying it.
 */
function Redeal({ view }: { view: TableView }): JSX.Element {
  const dealer = view.hand?.dealer ?? 0
  const next = (dealer + 1) % view.seatCount
  const deals = next === view.mySeat ? 'You deal' : `${view.name(next)} deals`
  return (
    <div className="explain" role="status">
      <h3 className="waiting-head">
        <b>Everyone passed</b>
      </h3>
      <p>Reshuffling — {deals} the new hand.</p>
    </div>
  )
}

/**
 * The headline above the ladder. Three situations were sharing one sentence,
 * and the one it was written for is the least common: with no legal calls the
 * panel said "Your call -- nothing is high enough to bid over the current
 * call", which is true when you have been outbid past the top of the ladder,
 * and simply wrong for the whole time you are waiting on somebody else, which
 * is three turns in four.
 */
function Explain({
  view,
  selected,
  passOnly,
  coach = null,
}: {
  view: TableView
  selected: LegalCall | null
  passOnly: boolean
  /** Coach mode: whether its pick is the selected call, a pass, or another. */
  coach?: 'this' | 'pass' | 'other' | null
}): JSX.Element {
  const tag = coach === 'this' ? <span className="coach-tag-inline">Coach&apos;s pick</span>
    : coach === 'pass' ? <span className="coach-tag-inline">Coach: pass</span>
    : null
  if (selected) {
    return (
      <div className="explain">
        <h3>{view.gameUi.explainHeadline(selected)}{tag}</h3>
        <p>
          <SuitText text={selected.explain} />
        </p>
      </div>
    )
  }
  return (
    <div className="explain">
      <h3>{passOnly ? 'Your turn — you can only pass' : 'Your turn to bid'}{tag}</h3>
      <p>
        {passOnly
          ? 'Nothing you could bid is high enough to beat the standing call.'
          : 'Choose a call.'}
      </p>
    </div>
  )
}

/**
 * The table during the auction: the four seats where they actually sit, each
 * holding its calls as tiles on its hand, the bidder lit and the rest dimmed
 * (Trickster's `.player.current` and fan overlay — see ui.css). The positions
 * are the felt's own `.seat-n/w/e/s`, so the auction and the play are the same
 * table. The viewer's own seat is drawn here, which it never is during play:
 * in the auction they are a bidder like the other three.
 */
function AuctionTable({
  view,
  closing,
  redealing,
}: {
  view: TableView
  closing: AuctionClose | null
  redealing: boolean
}): JSX.Element {
  const [settings] = useSettings()
  const strip = view.gameUi.auctionStrip(view)
  const history = view.gameUi.auctionCalls(view)
  const spoke = closing ? null : justCalled(view)
  // Once the auction has closed nobody is bidding, whoever the server now has
  // to act; the seat that won it is the one left lit.
  const bidder = closing || redealing ? null : view.toAct
  const winner = closing?.contractor ?? null
  const lit = closing ? winner : bidder
  // The hands are there for the whole auction, as small tokens: the table is
  // the height the ladder leaves it on every call, not only on yours, so it
  // never changes shape as the turn goes round.
  return (
    <section className="felt auction-felt">
      <div className="ring">
        {(['n', 'w', 'e', 's'] as const).map((position) => {
          const seat = view.at(position)
          if (!seat) return null
          // Trickster clears every call but the winner's when the auction
          // closes, so the one that counts is the one left on the table.
          const won = closing !== null && winner === seat.seat
          const calls = closing ? (won ? history[seat.seat]?.slice(-1) ?? [] : []) : (history[seat.seat] ?? [])
          const last = calls[calls.length - 1]
          const classes = [
            'seat',
            `seat-${position}`,
            'bid-seat',
            lit === seat.seat ? 'turn' : 'waiting',
            last?.pass ? 'passed' : '',
          ]
            .filter(Boolean)
            .join(' ')
          const holding = seat.holding
          const fan =
            !seat.isMe && holding?.kind === 'count' && holding.count > 0 ? (
              <SeatFan count={holding.count} position={position} lit={lit === null || lit === seat.seat} deck={settings.deck} />
            ) : null
          return (
            <div key={position} className={classes}>
              <div className="seat-label">
                <div className="seat-name">
                  {seat.isBot && !seat.isMe ? <BotIcon /> : null}
                  {seat.isMe ? 'You' : seat.name}
                </div>
              </div>
              <div className={fan ? 'seat-hand' : 'seat-hand bare'}>
                {fan}
                <BidTiles calls={calls} position={position} fresh={seat.seat === spoke} />
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

/**
 * A seat's calls, as Trickster lays them: tiles on the caller's own hand, like
 * cards put down in front of them. Each call is a square of card stock with
 * the call in its corner; a seat that bids again puts the new tile down half a
 * tile along from the last, so the corners of the earlier ones stay readable
 * and a raise is seen as a raise. A pass is a green tile, as theirs is, and in
 * 500 it is always the last one down. The standing bid has a brass edge — the
 * one mark here Trickster does not make, because the ladder is measured
 * against it. The newest tile slides in (their `show-bid`, 0.3 s).
 *
 * Side seats spread their tiles along their hand, which runs down the screen;
 * the call stays upright either way.
 */
function BidTiles({
  calls,
  position,
  fresh,
}: {
  calls: readonly AuctionSeatCall[]
  position: 'n' | 'e' | 's' | 'w'
  fresh: boolean
}): JSX.Element | null {
  if (calls.length === 0) return null
  const side = position === 'w' || position === 'e'
  return (
    <div className={side ? 'bid-tiles side' : 'bid-tiles'} style={{ '--calls': calls.length } as CSSProperties}>
      {calls.map((call, i) => {
        const newest = i === calls.length - 1
        const classes = ['bid-tile', call.pass ? 'pass' : '', call.high ? 'high' : '', newest && fresh ? 'new' : '']
          .filter(Boolean)
          .join(' ')
        return (
          <div key={i} className={classes} style={{ '--k': i } as CSSProperties}>
            <span>{call.pass ? 'Pass' : call.label}</span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * The closed auction, in the middle of the table: who won it and with what,
 * and the kitty going to them — Trickster's end of the auction, where every
 * other bid is cleared, the winner's stays on their name, and the kitty is
 * dealt into their hand before anyone is asked to do anything.
 *
 * The kitty is drawn as its backs, face down in the middle, which then fly to
 * the winner's fan (or down into your own hand). How many is read off the
 * table rather than off the rules: whatever the winner now holds over a hand.
 */
function AuctionWon({ view, closing }: { view: TableView; closing: AuctionClose }): JSX.Element {
  const [settings] = useSettings()
  const label = closing.contractor === null ? null : view.gameUi.auctionStrip(view)[closing.contractor]?.label
  const seat = closing.contractor === null ? null : view.seats[closing.contractor]
  const you = seat?.isMe === true
  const held = seat === null || seat === undefined ? 0
    : you ? view.myCards.length
    : seat.holding?.kind === 'count' ? seat.holding.count : 0
  // Counted once, as the close is first shown: the winner's hand shrinks again
  // when they discard, and the pile must not vanish mid-flight if they do.
  const [kitty] = useState(() => Math.max(0, held - view.tricksPerHand))
  const pile = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const el = pile.current
    if (!el || !seat || prefersReducedMotion()) return
    const ring = document.querySelector('.auction-felt .ring')
    const target = you
      ? document.querySelector('.hand-band .hand')
      : ring?.querySelector(`.seat-${seat.position} .seat-fan-cards`) ?? ring?.querySelector(`.seat-${seat.position}`)
    if (!target) return
    const to = target.getBoundingClientRect()
    const timers: number[] = []
    el.querySelectorAll<HTMLElement>('img').forEach((card, i) => {
      if (typeof card.animate !== 'function') return
      timers.push(
        window.setTimeout(() => {
          const from = card.getBoundingClientRect()
          const dx = to.left + to.width / 2 - (from.left + from.width / 2)
          const dy = to.top + to.height / 2 - (from.top + from.height / 2)
          card.animate(
            [
              { transform: card.style.transform || 'none', opacity: 1 },
              { transform: `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) scale(.55)`, opacity: 0 },
            ],
            { duration: 640, easing: 'cubic-bezier(0.55, 0.00, 0.20, 1.00)', fill: 'forwards' },
          )
        }, 1100 + i * 140),
      )
    })
    return () => timers.forEach((t) => window.clearTimeout(t))
    // Once, when the close is first shown.
  }, [])

  return (
    <div className="explain won" role="status">
      <h3>
        <span>{you ? <b>You win the bid</b> : <><b>{seat?.name ?? 'Nobody'}</b> wins the bid</>}</span>
        <span className="bid-chip bid on-brass won-chip">{label ?? closing.label}</span>
      </h3>
      <p className="won-line">
        {kitty > 0 ? (
          <>
            <span className="won-kitty" ref={pile} aria-hidden="true">
              {Array.from({ length: kitty }, (_, i) => (
                <img
                  key={i}
                  src={cardSrc('BACK', settings.deck)}
                  alt=""
                  style={{ transform: `translateX(${(i - (kitty - 1) / 2) * 8}px) rotate(${(i - (kitty - 1) / 2) * 6}deg)` }}
                />
              ))}
            </span>
            {you ? 'The kitty is yours' : `${seat?.name ?? ''} picks up the kitty`}
          </>
        ) : (
          '\u00A0'
        )}
      </p>
    </div>
  )
}

/**
 * The seat that has just spoken, or null.
 *
 * From the event stream rather than from diffing snapshots, which is what
 * `events` is for: "The client's state is always the snapshot; events are
 * advisory and exist so the UI can animate *what changed*" (core/events.ts).
 * A bid landing with no motion is the whole problem — the table quietly
 * becomes a slightly different table, and nothing says a turn went by.
 */
function justCalled(view: TableView): Seat | null {
  for (const tagged of view.snapshot.events) {
    if (tagged.event.type === 'called') return tagged.event.seat
  }
  return null
}
